import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Helper function to get the full logo URL
const getLogoUrl = (logoPath) => {
  if (!logoPath) return '';
  // If it's already a full URL, return it as is
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  // Extract base URL (without /api) and prepend to logo path
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${logoPath}`;
};

const LeadDetails = ({ lead, onBack, onRefresh }) => {
  const [leadData, setLeadData] = useState(lead);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    status: lead.status || 'lead',
    notes: lead.notes || '',
    customerId: lead.customerId || '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchLeadDetails();
  }, [lead._id]);

  const fetchLeadDetails = async () => {
    try {
      const data = await apiRequest(`/admin/leads/${lead._id}`);
      if (data && data.success) {
        setLeadData(data.data);
        setFormData({
          status: data.data.status || 'lead',
          notes: data.data.notes || '',
          customerId: data.data.customerId || '',
        });
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await apiRequest(`/admin/leads/${lead._id}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });

      if (data && data.success) {
        setMessage({ type: 'success', text: 'تم تحديث البيانات بنجاح' });
        setLeadData(data.data);
        setEditMode(false);
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ أثناء التحديث' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      lead: { label: 'طلب جديد', class: 'badge-info' },
      setup: { label: 'قيد الإعداد', class: 'badge-warning' },
      converted: { label: 'تم التحويل', class: 'badge-success' },
      rejected: { label: 'مرفوض', class: 'badge-danger' },
    };
    const statusInfo = statusMap[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  if (loading && !leadData) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">تفاصيل الطلب</h2>
            <button className="btn btn-secondary btn-sm" onClick={onBack}>
              رجوع
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            <span>{message.type === 'success' ? '✅' : '❌'}</span>
            <span>{message.text}</span>
          </div>
        )}

        <div className="card-body">
          {!editMode ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
                <div>
                  <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>البيانات الأساسية</h3>
                  <p><strong>اسم صاحب المتجر:</strong> {leadData.storeOwnerName}</p>
                  <p><strong>اسم المتجر:</strong> {leadData.storeName}</p>
                  <p><strong>البريد الإلكتروني:</strong> {leadData.email}</p>
                  <p><strong>رقم الهاتف:</strong> {leadData.phoneNumber}</p>
                  <p><strong>لديه دومين:</strong> {leadData.hasDomain ? 'نعم' : 'لا'}</p>
                  <p><strong>الدومين:</strong> {leadData.domain || '—'}</p>
                  <p><strong>المصدر:</strong> {leadData.source === 'lead-generation' ? 'طلب أولي' : 'إعداد كامل'}</p>
                  <p><strong>الحالة:</strong> {getStatusBadge(leadData.status)}</p>
                </div>

                {leadData.source === 'initial-setup' && (
                  <div>
                    <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>معلومات المتجر</h3>
                    <p><strong>هاتف المتجر:</strong> {leadData.storePhone || '—'}</p>
                    <p><strong>عنوان المتجر:</strong> {leadData.storeAddress || '—'}</p>
                    <p><strong>وصف المتجر:</strong> {leadData.storeDescription || '—'}</p>
                    <p><strong>شعار المتجر:</strong> {leadData.storeTagline || '—'}</p>
                    {leadData.logo && (
                      <div>
                        <strong>الشعار:</strong>
                        <img src={getLogoUrl(leadData.logo)} alt="Logo" style={{ maxWidth: '200px', marginTop: '0.5rem', display: 'block' }} />
                      </div>
                    )}
                    <p><strong>نوع النشاط:</strong> {
                      leadData.businessType === 'clothes' ? 'ملابس' :
                      leadData.businessType === 'electronics' ? 'إلكترونيات' :
                      leadData.businessType === 'home-accessories' ? 'إكسسوارات منزلية' : '—'
                    }</p>
                    <p><strong>ساعات العمل - أيام الأسبوع:</strong> {leadData.storeHoursWeekdays || '—'}</p>
                    <p><strong>ساعات العمل - نهاية الأسبوع:</strong> {leadData.storeHoursWeekends || '—'}</p>
                  </div>
                )}

                {leadData.source === 'initial-setup' && (
                  <div>
                    <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>الألوان</h3>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {leadData.primaryColor && (
                        <div>
                          <strong>الأساسي:</strong>
                          <div style={{ width: '50px', height: '50px', backgroundColor: leadData.primaryColor, border: '2px solid #ddd', borderRadius: '8px', marginTop: '0.5rem' }} />
                        </div>
                      )}
                      {leadData.secondaryColor && (
                        <div>
                          <strong>الثانوي:</strong>
                          <div style={{ width: '50px', height: '50px', backgroundColor: leadData.secondaryColor, border: '2px solid #ddd', borderRadius: '8px', marginTop: '0.5rem' }} />
                        </div>
                      )}
                      {leadData.accentColor && (
                        <div>
                          <strong>التمييز:</strong>
                          <div style={{ width: '50px', height: '50px', backgroundColor: leadData.accentColor, border: '2px solid #ddd', borderRadius: '8px', marginTop: '0.5rem' }} />
                        </div>
                      )}
                      {leadData.backgroundColor && (
                        <div>
                          <strong>الخلفية:</strong>
                          <div style={{ width: '50px', height: '50px', backgroundColor: leadData.backgroundColor, border: '2px solid #ddd', borderRadius: '8px', marginTop: '0.5rem' }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #f0f0f0' }}>
                <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>ملاحظات</h3>
                <p>{leadData.notes || 'لا توجد ملاحظات'}</p>
              </div>

              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                  تعديل
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">الحالة</label>
                <select name="status" className="form-control" value={formData.status} onChange={handleChange}>
                  <option value="lead">طلب جديد</option>
                  <option value="setup">قيد الإعداد</option>
                  <option value="converted">تم التحويل</option>
                  <option value="rejected">مرفوض</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">ملاحظات</label>
                <textarea
                  name="notes"
                  className="form-control"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="5"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'جاري الحفظ...' : 'حفظ'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)}>
                  إلغاء
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;

